import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  CommandEmpty,
  CommandGroup,
  CommandGroupLabel,
  CommandItem,
  CommandSeparator,
  Icon,
  Text,
  Toggle,
  toastManager,
  useIsMobile,
  useTheme,
  type IconName,
} from "@salestrak/ui";

import { useMutation } from "@apollo/client/react";
import { usePathname } from "#app/lib/hooks/router-compat";

import { ENTITY_KIND_ICON, type EntityData } from "#app/lib/entity-visuals";
import { ResolveAddressSuggestionDocument } from "#app/lib/data";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { EntityTag, useEntityDetailOptional } from "#app/components/entity";
import { useIssueViewOptional } from "#app/components/issue-view";
import { useWorkflowId } from "#app/lib/hooks/use-workflow-id";
import {
  useGlobalSearch,
  KIND_TO_SECTION,
  ALL_KINDS,
  type GlobalSearchHit,
  type SearchKind,
} from "#app/lib/hooks/use-global-search";
import { useRecentRecords } from "#app/lib/hooks/use-recent-records";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAnalytics, AnalyticsContext } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";

type CommandGroupName = "Quick actions" | "Go to" | "More";

// Order the empty-state launcher renders its command groups in.
const COMMAND_GROUP_ORDER: CommandGroupName[] = ["Quick actions", "Go to", "More"];

// Scoped-search filter chips (multi-select, none selected by default). No
// selection = every kind shows; selecting chips narrows the list client-side.
// Workflow scope only applies while issues are in view (no chips, or Projects
// selected).
// Filter chips double as the result-section order + headings (value = kind,
// label = section name).
const SEARCH_FILTERS: { value: SearchKind; label: string }[] = [
  { value: "issue", label: "Projects" },
  { value: "address", label: "Addresses" },
  { value: "user", label: "People" },
  { value: "team", label: "Teams" },
];

// Chip/row icon per kind. Entity kinds come from ENTITY_KIND_ICON; "address" is
// not an entity kind, so it maps to a map pin here.
const FILTER_ICON: Record<SearchKind, IconName> = {
  issue: ENTITY_KIND_ICON.issue,
  user: ENTITY_KIND_ICON.user,
  team: ENTITY_KIND_ICON.team,
  address: "map-pin",
};

// Kind → singular footer/badge label. One source for the ladder that otherwise
// diverges across `contextByValue` and `renderHit`.
const KIND_LABEL: Record<SearchKind, string> = {
  issue: "Project",
  user: "Member",
  team: "Team",
  address: "Address",
};

// Generic user-settings key the chip selection persists under.
const SEARCH_CHIP_FILTERS_KEY = "global_search_chip_filters";

// Loose JSON from the settings bag → a validated list of kinds.
function normalizeKinds(value: unknown): SearchKind[] {
  if (!Array.isArray(value)) return [];
  const valid = new Set<string>(SEARCH_FILTERS.map((f) => f.value));
  return value.filter(
    (k): k is SearchKind => typeof k === "string" && valid.has(k),
  );
}

type CommandDef = Omittable<{
  id: string;
  icon: IconName;
  label: string;
  keywords?: string[];
  action: () => void;
  group: CommandGroupName;
  /** Secondary line shown beneath the label on Quick-action rows. */
  sublabel?: string;
  /** Right-aligned hint on Go to / More rows (a route or "action"). */
  meta?: string;
}>;

// `openCreateIssue` is absent on surfaces with no create affordance, and the
// command list already reads it as a truthiness check — so the member relaxes.
function useCommands(opts: Omittable<{
  navigate: (route: string) => void;
  toggleTheme: () => void;
  openFeedback: () => void;
  openCreateIssue?: () => void;
  themeLabel: string;
}>): CommandDef[] {
  const { navigate, openCreateIssue, toggleTheme, openFeedback, themeLabel } =
    opts;
  return useMemo(
    (): CommandDef[] => [
      // Quick actions — rendered as command rows in the launcher.
      ...(openCreateIssue
        ? [
            {
              id: "create-issue",
              icon: "plus" as const,
              label: "Create new project",
              keywords: ["create", "new", "project"],
              action: openCreateIssue,
              group: "Quick actions" as const,
              sublabel: "New record",
            },
          ]
        : []),
      {
        id: "go-maps",
        icon: "map-pin",
        label: "Open map",
        keywords: ["map", "maps", "location", "canvass"],
        action: () => navigate("/map"),
        group: "Quick actions",
        sublabel: "Canvass & routes",
      },
      // Go to — jump-to destinations, each with its real route.
      {
        id: "go-inbox",
        icon: "inbox",
        label: "Inbox",
        keywords: ["inbox", "messages", "notifications"],
        action: () => navigate("/inbox"),
        group: "Go to",
        meta: "/inbox",
      },
      {
        id: "go-members",
        icon: "user",
        label: "Members",
        keywords: ["members", "people"],
        action: () => navigate("/members"),
        group: "Go to",
        meta: "/members",
      },
      {
        id: "go-teams",
        icon: "users",
        label: "Teams",
        keywords: ["teams", "groups"],
        action: () => navigate("/teams"),
        group: "Go to",
        meta: "/teams",
      },
      {
        id: "go-preferences",
        icon: "sliders-horizontal",
        label: "Preferences",
        keywords: ["preferences"],
        action: () => navigate("/settings/preferences"),
        group: "Go to",
        meta: "/settings/preferences",
      },
      // More — utility commands, kept last.
      {
        id: "toggle-theme",
        icon: "sun-moon",
        label: themeLabel,
        keywords: ["theme", "dark", "light", "mode"],
        action: toggleTheme,
        group: "More",
        meta: "action",
      },
      {
        id: "report-bug",
        icon: "message-square",
        label: "Report a bug",
        keywords: ["bug", "feedback", "report"],
        action: openFeedback,
        group: "More",
        meta: "action",
      },
    ],
    [
      navigate,
      openCreateIssue,
      toggleTheme,
      openFeedback,
      themeLabel,
    ],
  );
}

function filterCommands(commands: CommandDef[], query: string): CommandDef[] {
  if (!query.trim()) return commands;
  const lower = query.toLowerCase();
  return commands.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(lower) ||
      cmd.keywords?.some((kw) => kw.includes(lower)),
  );
}

/**
 * A single result row. `highlighted` is the Command root's highlight — Base UI
 * moves it on both arrow keys and pointer hover, so one flag drives the kind
 * badge (Project/Member/Team) + chevron; an unhighlighted row shows the optional
 * trailing `marker` (the issue pinned/recent star/clock) instead.
 */
function ResultRow({
  value,
  highlighted,
  entity,
  description,
  kindLabel,
  marker,
  onSelect,
}: Omittable<{
  value: string;
  highlighted: boolean;
  entity: EntityData;
  description?: string;
  kindLabel: string;
  marker?: React.ReactNode;
  onSelect: () => void;
}>) {
  return (
    <CommandItem value={value} onClick={onSelect}>
      <Box direction="row" align="center" gap="sm" fill="width" justify="between">
        <Box shrink direction="row" align="center" minWidth={0}>
          <EntityTag
            entity={entity}
            size="md"
            weight="regular"
            padded
            interactive={false}
            ghost={false}
            description={description}
          />
        </Box>
        {highlighted ? (
          <Box direction="row" align="center" gap="xs">
            <Badge size="sm" variant="secondary">
              {kindLabel}
            </Badge>
            <Icon name="chevron-right" size="xs" color="muted" />
          </Box>
        ) : (
          marker ?? null
        )}
      </Box>
    </CommandItem>
  );
}

/**
 * A geocoded address result row (not an entity — a Google Places suggestion), so
 * it renders a map-pin + the address text rather than an EntityTag. Selecting it
 * resolves the place and opens the map there.
 */
function AddressRow({
  value,
  highlighted,
  description,
  onSelect,
}: {
  value: string;
  highlighted: boolean;
  description: string;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={value} onClick={onSelect}>
      <Box direction="row" align="center" gap="sm" fill="width" justify="between">
        <Box shrink direction="row" align="center" gap="sm" minWidth={0}>
          <Icon name="map-pin" size="sm" color="muted" />
          <Text size="sm" truncate={1}>
            {description}
          </Text>
        </Box>
        {highlighted ? (
          <Box direction="row" align="center" gap="xs">
            <Badge size="sm" variant="secondary">
              Create project
            </Badge>
            <Icon name="chevron-right" size="xs" color="muted" />
          </Box>
        ) : null}
      </Box>
    </CommandItem>
  );
}

export type GlobalSearchContent = Omittable<{
  query: string;
  setQuery: (value: string) => void;
  loading: boolean;
  /** Results + commands list, to render inside a CommandList. */
  itemsContent: React.ReactNode;
  /** Scoped-search header (kind chips + workflow scope toggle). Rendered above
   *  the list, in flow. */
  scopeBar: React.ReactNode;
  /** Short context label for the highlighted row (e.g. "Project", "Navigate"),
   *  shown on the footer's right side. */
  footerContext?: string;
  /** Wire onto the Command root so the hook can track the highlighted row. */
  onItemHighlighted: (value: unknown) => void;
  /** Attach to the CommandList so a new search scrolls back to the top. */
  listRef: React.RefObject<HTMLDivElement | null>;
  feedbackOpen: boolean;
  setFeedbackOpen: (open: boolean) => void;
}>;

/**
 * Shared controller for the global search surfaces (desktop header bar and
 * mobile drawer). Owns the query, the command list, and the result/command
 * rendering. Every selectable action calls `onClose()` first so each surface can
 * dismiss itself (clear+blur the bar on desktop; close the drawer on mobile).
 */
export function useGlobalSearchContent({
  onClose,
  focusInput,
}: Omittable<{
  onClose: () => void;
  /** Return focus to the search input (e.g. after a chip click) so keyboard
   *  navigation keeps working — Base UI binds it to the input. */
  focusInput?: () => void;
}>): GlobalSearchContent {
  const { track } = useAnalytics();
  const issueView = useIssueViewOptional();
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const { push: appPush } = useAppNavigate();
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  // Selecting a project pans the map (instead of opening the sidebar) only when
  // the search is invoked from the map screen — see navigateToIssue.
  const pathname = usePathname();
  const onMap = pathname?.endsWith("/map") ?? false;

  // Base UI owns the highlight; `onItemHighlighted` mirrors it here so rows can
  // render their selected affordances and the footer can label the active row.
  // The highlight follows BOTH arrow keys and pointer hover.
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
  const onItemHighlighted = useCallback((value: unknown) => {
    setHighlightedValue(typeof value === "string" ? value : null);
  }, []);

  // Scoped search: kind filter chips + a contextual "in this workflow" scope.
  // Empty = no kind filter (everything shows). Persisted in the generic
  // user-settings bag via useUserSetting, which writes through the Apollo cache
  // — so the selection is remembered across refreshes AND survives a remount (or
  // the sibling desktop-bar / mobile-drawer consumer) without reverting to a
  // stale cached value.
  const { value: selectedKinds, setValue: setSelectedKinds } = useUserSetting(
    SEARCH_CHIP_FILTERS_KEY,
    normalizeKinds,
  );
  const toggleKind = useCallback(
    (kind: SearchKind) => {
      const next = selectedKinds.includes(kind)
        ? selectedKinds.filter((entry) => entry !== kind)
        : [...selectedKinds, kind];
      setSelectedKinds(next);
      // Toggling a chip moves focus to it; hand focus back to the input so
      // arrow-key navigation (handled on the input) keeps working.
      focusInput?.();
    },
    [selectedKinds, setSelectedKinds, focusInput],
  );
  // The workflow currently being viewed (if any) — the contextual scope target.
  const contextWorkflowId = useWorkflowId();
  // Scope defaults OFF (global search); the chip toggles it on to limit results
  // to the workflow currently in view.
  const [scopeOn, setScopeOn] = useState(false);
  // Workflow scope only bites while issues are in view — no kind filter, or the
  // Projects chip selected. When only People/Teams are selected it's hidden.
  const filterIncludesIssues =
    selectedKinds.length === 0 || selectedKinds.includes("issue");
  const scopeAvailable = !!contextWorkflowId && filterIncludesIssues;
  const scopeActive = scopeAvailable && scopeOn;
  // The server returns hits grouped by kind (no ranking); each section (items +
  // total) paginates independently via the hook's `loadMore`.
  const { query, setQuery, debouncedQuery, results, loadMore, loading } =
    useGlobalSearch({
      scopeWorkflowId: scopeActive ? contextWorkflowId : undefined,
      // Only fetch the kinds actually in view — a filtered-out kind (incl. the
      // paid Google Places addresses call) is never queried.
      kinds: selectedKinds,
    });

  const section = useCallback(
    (kind: SearchKind) => results[KIND_TO_SECTION[kind]],
    [results],
  );

  // Sections to render: all when no chips are selected, else just the selected
  // ones. Preserves SEARCH_FILTERS order.
  const visibleFilters =
    selectedKinds.length === 0
      ? SEARCH_FILTERS
      : SEARCH_FILTERS.filter((f) => selectedKinds.includes(f.value));

  // A genuine new result set — a new query or a scope toggle — scrolls back to
  // the top. "Load more" appends below and must NOT move the viewport, so this
  // keys on the query/scope pair only. The list lives inside the library's
  // ScrollArea, so the scrolling element is that viewport, not the list itself.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const viewport = listRef.current?.closest<HTMLElement>(
      "[data-slot=scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = 0;
  }, [debouncedQuery, scopeActive]);

  // "Load more" fetches the section's next page server-side, then returns focus
  // to the input so arrow-key navigation keeps working.
  const onLoadMore = useCallback(
    (kind: SearchKind) => {
      loadMore(kind);
      focusInput?.();
    },
    [loadMore, focusInput],
  );

  // Members/teams open the shared entity detail (same as clicking the chip).
  const entityDetail = useEntityDetailOptional();

  // Selecting an address resolves the Google place to lat/lng, then: (1) opens
  // the map focused there — finds-or-creates a pin and pops the infobox (the
  // map's `focusLat`/`focusLng` one-shot) — and, on desktop only, (2) opens the
  // create-project issue-view modal (the sidebar) prefilled with the address. On
  // mobile we just land on the map (no create sheet). Falls back to the plain
  // map on error.
  const [resolveAddress] = useMutation(ResolveAddressSuggestionDocument);
  const selectAddress = useCallback(
    async (placeId: string, description: string) => {
      onClose();
      try {
        const { data } = await resolveAddress({
          variables: { placeId, description },
        });
        const resolved = data?.resolveAddressSuggestion;
        if (resolved?.latLng) {
          const params = new URLSearchParams({
            focusLat: String(resolved.latLng.lat),
            focusLng: String(resolved.latLng.lng),
          });
          if (resolved.addressId) params.set("addressId", resolved.addressId);
          appPush(`/map?${params.toString()}`);
          if (!isMobile) {
            // Prefill the title from the Melissa property record, same as the
            // map's "add project" action (map.tsx handleAddProject). The
            // ResolveAddressSuggestion mutation already selects propertyDetails
            // — it was simply never read here, so picking an address in global
            // search opened an untitled draft while the identical action on the
            // map arrived pre-titled with the owner's name.
            const ownerName = resolved.propertyDetails?.ownerNames?.[0];
            issueView?.openCreateIssue({
              source: "global_search",
              address: {
                wholeAddress: resolved.wholeAddress ?? description,
                latLng: { lat: resolved.latLng.lat, lng: resolved.latLng.lng },
              },
              name: ownerName || resolved.wholeAddress || description,
            });
          }
          return;
        }
        // Resolved but no coordinates — tell the user rather than silently
        // dropping them on the default map with no sign the pick failed.
        toastManager.add({
          title: "Couldn't locate that address",
          type: "error",
        });
      } catch (err) {
        toastMutationError(err, "Failed to open address");
      }
      appPush("/map");
    },
    [onClose, resolveAddress, appPush, issueView, isMobile],
  );

  const onOpenFeedback = useCallback(() => {
    onClose();
    setFeedbackOpen(true);
  }, [onClose]);

  const onCreateIssue = useCallback(() => {
    track({
      context: AnalyticsContext.Search,
      event: "command",
      label: "create_issue",
    });
    onClose();
    issueView?.openCreateIssue({ source: "global_search" });
  }, [onClose, issueView, track]);

  const navigate = useCallback(
    (route: string) => {
      onClose();
      appPush(route);
    },
    [onClose, appPush],
  );

  const navigateToIssue = useCallback(
    (
      workflowId: string,
      issueId: string,
      coords?: { lat: number | null; lng: number | null },
    ) => {
      track({
        context: AnalyticsContext.Search,
        event: "select_result",
        label: "global_search",
      });
      onClose();
      // When searching from the map, selecting a project pans the map to its
      // pin instead of opening the project sidebar — the map's `focusLat`/`focusLng` one-shot finds-or-
      // creates the pin and pops its infobox. Anywhere else, open the project.
      if (
        onMap &&
        coords &&
        Number.isFinite(coords.lat) &&
        Number.isFinite(coords.lng)
      ) {
        appPush(`/map?focusLat=${coords.lat}&focusLng=${coords.lng}`);
        return;
      }
      if (issueView?.openIssueById) {
        issueView.openIssueById(issueId);
      } else {
        appPush(`/workflow/${workflowId}?issue=${issueId}`);
      }
    },
    [onClose, appPush, issueView, track, onMap],
  );

  const toggleTheme = useCallback(() => {
    onClose();
    setTheme(theme === "dark" ? "light" : "dark");
  }, [onClose, theme, setTheme]);

  const themeLabel = useMemo(
    () => `Toggle theme to ${theme === "dark" ? "light" : "dark"}`,
    [theme],
  );

  const commands = useCommands({
    navigate,
    openCreateIssue: issueView ? onCreateIssue : undefined,
    toggleTheme,
    openFeedback: onOpenFeedback,
    themeLabel,
  });

  const filteredCommands = useMemo(
    () => filterCommands(commands, query),
    [commands, query],
  );
  const showResults = debouncedQuery.length > 0;

  const { favoriteIds } = useWorkspaceData();
  const recentIds = useRecentRecords();
  const recentSet = useMemo(() => new Set(recentIds), [recentIds]);

  // Maps each rendered CommandItem value to a short context label the footer
  // shows on the right for whatever row is highlighted, labelled by kind.
  const contextByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const kind of ALL_KINDS) {
      for (const hit of results[KIND_TO_SECTION[kind]].items) {
        map[`${hit.kind}-${hit.id}`] = KIND_LABEL[hit.kind as SearchKind];
      }
    }
    for (const cmd of commands) {
      map[cmd.id] =
        cmd.group === "Quick actions"
          ? "Action"
          : cmd.group === "Go to"
            ? "Navigate"
            : "Command";
    }
    return map;
  }, [results, commands]);

  // One renderer for every hit. The server groups by kind and returns each
  // section in source order, so we render in order and just dispatch the chip +
  // select behaviour by kind. Issues show a pinned/recent marker (a client-only
  // signal the server doesn't know about) without reordering.
  const renderHit = (hit: GlobalSearchHit, index: number) => {
    const value = `${hit.kind}-${hit.id}`;
    // React key appends the array index — server-aggregated cross-kind hits can
    // carry duplicate ids (dedup gaps across sources), and keying purely by id
    // would then throw "two children with the same key".
    const key = `${value}-${index}`;
    const description = hit.subtitle ?? undefined;
    const highlighted = highlightedValue === value;
    // Only the ResultRow branches (issue/user/team) read this; the address
    // branch returns early with its own "Create project" badge.
    const kindLabel = KIND_LABEL[hit.kind as SearchKind];

    // Addresses are Google Places suggestions (not entities): a map-pin + text
    // row that resolves + opens the map on select.
    if (hit.kind === "address") {
      return (
        <AddressRow
          key={key}
          value={value}
          highlighted={highlighted}
          description={hit.name}
          onSelect={() => selectAddress(hit.id, hit.name)}
        />
      );
    }

    if (hit.kind === "issue") {
      const isFavorite = favoriteIds.has(hit.id);
      const flagged = isFavorite || recentSet.has(hit.id);
      const entity: EntityData = {
        kind: "issue",
        id: hit.id,
        issue: { name: hit.name, icon: { icon: hit.iconName, color: hit.iconColor } },
        workflowId: hit.workflowId ?? undefined,
      };
      return (
        <ResultRow
          key={key}
          value={value}
          highlighted={highlighted}
          entity={entity}
          description={description}
          kindLabel={kindLabel}
          marker={
            flagged ? (
              <Icon
                name={isFavorite ? "star" : "clock"}
                size="xs"
                color={isFavorite ? "warning" : "muted"}
              />
            ) : undefined
          }
          onSelect={() =>
            navigateToIssue(hit.workflowId ?? "", hit.id, {
              lat: hit.lat ?? null,
              lng: hit.lng ?? null,
            })
          }
        />
      );
    }

    // Member / team rows — rendered through the entity chip; selecting opens
    // that entity's detail.
    const entity: EntityData =
      hit.kind === "team"
        ? {
            kind: "team",
            id: hit.id,
            team: { teamName: hit.name, icon: { icon: hit.iconName, color: hit.iconColor } },
          }
        : {
            kind: "user",
            id: hit.id,
            user: { name: hit.name, email: hit.subtitle, profileImage: hit.profileImage },
          };
    return (
      <ResultRow
        key={key}
        value={value}
        highlighted={highlighted}
        entity={entity}
        description={description}
        kindLabel={kindLabel}
        onSelect={() => {
          onClose();
          entityDetail?.openEntity(hit.kind === "team" ? "team" : "user", hit.id);
        }}
      />
    );
  };

  // Every command (Quick action, Go to, More) renders as a keyboard-navigable
  // CommandItem row: a tinted rounded-square icon container + label (with the
  // optional Quick-action sublabel stacked beneath), plus a route chip + chevron
  // on navigable rows. Rendering quick actions as CommandItems (not standalone
  // pressable tiles) keeps them reachable by arrow keys + Enter, not mouse-only.
  const renderCommand = (cmd: CommandDef, index: number) => {
    const isNav = cmd.group === "Go to";
    return (
      // Key appends the array index per the list-key rule (never key purely by id).
      <CommandItem key={`${cmd.id}-${index}`} value={cmd.id} onClick={cmd.action}>
        <Box direction="row" align="center" gap="sm" fill="width" justify="between">
          <Box direction="row" align="center" gap="sm" shrink minWidth={0}>
            <Box
              width={28}
              height={28}
              rounded="md"
              background="muted"
              align="center"
              justify="center"
            >
              <Icon name={cmd.icon} size="sm" color="muted" />
            </Box>
            <Box direction="column" shrink minWidth={0}>
              <Text size="sm" truncate={1}>
                {cmd.label}
              </Text>
              {cmd.sublabel ? (
                <Text size="xs" variant="muted" truncate={1}>
                  {cmd.sublabel}
                </Text>
              ) : null}
            </Box>
          </Box>
          <Box direction="row" align="center" gap="xs">
            {cmd.meta && cmd.meta !== "action" ? (
              <Box background="muted" rounded="sm" paddingX="2xs">
                <Text size="xs" variant="muted">
                  {cmd.meta}
                </Text>
              </Box>
            ) : cmd.meta === "action" ? (
              <Text size="xs" variant="muted">
                action
              </Text>
            ) : null}
            {isNav ? <Icon name="chevron-right" size="xs" color="muted" /> : null}
          </Box>
        </Box>
      </CommandItem>
    );
  };

  const itemsContent = showResults ? (
    // Keyed by the selected chips so changing the filter REMOUNTS the whole
    // list: items re-register in DOM order, so keyboard-nav order matches what
    // is on screen. "Load more" only grows a section's shown count (no
    // remount), so it leaves the highlight alone.
    (<React.Fragment key={`filter-${selectedKinds.join(",")}`}>
      {visibleFilters.map(({ value: kind, label }) => {
        const { items, total } = section(kind);
        if (items.length === 0) return null;
        return (
          <CommandGroup key={kind}>
            <CommandGroupLabel>
              {label}{" "}
              <Text size="2xs" variant="muted">
                (Showing {items.length} of {total})
              </Text>
            </CommandGroupLabel>
            {items.map(renderHit)}
            {items.length < total && (
              <CommandItem
                // Key on the section's contents so a new search / page
                // REMOUNTS this row — otherwise it persists with a stale
                // registration order and sorts above the fresh result rows.
                key={`more-${kind}-${items[0]?.id ?? ""}-${items.length}`}
                value={`more-${kind}`}
                onClick={() => onLoadMore(kind)}
              >
                <Box direction="row" align="center" gap="xs">
                  <Icon name="chevron-down" size="xs" color="muted" />
                  <Text size="sm" variant="muted">
                    Load more
                  </Text>
                </Box>
              </CommandItem>
            )}
          </CommandGroup>
        );
      })}
      {!loading &&
        visibleFilters.every((f) => section(f.value).items.length === 0) && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
      {filteredCommands.length > 0 && <CommandSeparator />}
      {/* One flat, filtered command list under the results. */}
      <CommandGroup>
        <CommandGroupLabel>Commands</CommandGroupLabel>
        {filteredCommands.map(renderCommand)}
      </CommandGroup>
    </React.Fragment>)
  ) : (
    // Empty state (the launcher): every group renders as keyboard-navigable
    // command rows so quick actions can be reached by arrow keys + Enter.
    (COMMAND_GROUP_ORDER.map((groupName) => {
      const groupCommands = filteredCommands.filter(
        (cmd) => cmd.group === groupName,
      );
      if (groupCommands.length === 0) return null;
      return (
        <CommandGroup key={groupName}>
          <CommandGroupLabel>{groupName}</CommandGroupLabel>
          {groupCommands.map(renderCommand)}
        </CommandGroup>
      );
    }))
  );

  // Scoped-search header: kind filter chips (left) + the contextual workflow-
  // scope toggle (right, only while issues are in view and we're inside a
  // workflow). Always shown (even on the empty launcher), in flow above the
  // list on every surface.
  const scopeBar = (
    <Box
      direction="row"
      align="center"
      justify="between"
      gap="sm"
      // Breathing room on every side: the row sits directly under the input's
      // divider, and the chips must not touch the panel edge.
      paddingX="base"
      paddingY="sm"
    >
      <Box direction="row" align="center" gap="xs">
        {SEARCH_FILTERS.map((filter) => {
          const isSelected = selectedKinds.includes(filter.value);
          return (
            <Toggle
              key={filter.value}
              size="sm"
              variant="outline"
              pressed={isSelected}
              onPressedChange={() => toggleKind(filter.value)}
            >
              <Icon name={FILTER_ICON[filter.value]} size="xs" />
              <Text size="xs" variant={isSelected ? undefined : "muted"}>
                {filter.label}
              </Text>
            </Toggle>
          );
        })}
      </Box>
      {scopeAvailable ? (
        <Toggle
          size="sm"
          variant="outline"
          pressed={scopeOn}
          onPressedChange={setScopeOn}
        >
          <Icon name={ENTITY_KIND_ICON.workflow} size="xs" />
          <Text size="xs" variant={scopeOn ? undefined : "muted"}>
            Only this workflow
          </Text>
        </Toggle>
      ) : null}
    </Box>
  );

  return {
    query,
    setQuery,
    loading,
    itemsContent,
    scopeBar,
    footerContext: highlightedValue
      ? contextByValue[highlightedValue]
      : undefined,
    onItemHighlighted,
    listRef,
    feedbackOpen,
    setFeedbackOpen,
  };
}
